import { Link } from "react-router-dom";
import { useLocale } from "@/contexts/LanguageContext";
import { hero } from "@/data/content";
import { Phone, Calendar, MessageCircle } from "lucide-react";
import { ProofBar } from "@/components/shared/ProofBar";
import officeInterior from "@/assets/office-interior.webp";

export function Hero() {
  const { locale, t, getLocalizedHref } = useLocale();

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden">
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${officeInterior})` }}
      />
      <div className="absolute inset-0 bg-gradient-to-r from-background/90 via-background/75 to-background/55" />
      <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-background/40" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_30%_50%,hsl(37_42%_60%_/_0.06),transparent_60%)]" />
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />

      <div className="relative container mx-auto max-w-7xl px-6 md:px-8 lg:px-10 py-32 md:py-40">
        <div className="mb-12">
          <Link
            to={getLocalizedHref("/client-portal")}
            className="inline-flex items-center gap-2.5 micro-label text-muted-foreground hover:text-primary transition-colors border border-border/50 rounded-full px-6 py-2.5"
          >
            {t(hero.portalLink)}
            <span aria-hidden="true">→</span>
          </Link>
        </div>

        <h1 className="font-serif text-5xl sm:text-6xl md:text-7xl lg:text-[5.5rem] font-extrabold text-foreground max-w-5xl leading-[1.04] mb-10 tracking-[-0.02em]">
          <span className="opacity-0 animate-fade-in-up" style={{ animationDelay: "0.1s", animationFillMode: "forwards" }}>
            {t(hero.headline)}
          </span>
        </h1>

        <p className="text-xl md:text-2xl text-muted-foreground max-w-2xl mb-16 opacity-0 animate-fade-in leading-relaxed" style={{ animationDelay: "0.4s", animationFillMode: "forwards" }}>
          {t(hero.subline)}
        </p>

        <div className="flex flex-col sm:flex-row gap-4 mb-20 opacity-0 animate-fade-in" style={{ animationDelay: "0.6s", animationFillMode: "forwards" }}>
          <Link
            to={getLocalizedHref("/contact")}
            className="inline-flex items-center justify-center gap-2.5 bg-ivory-cta text-ivory-cta-foreground px-12 py-5 rounded-md text-lg font-semibold hover:bg-primary hover:text-primary-foreground transition-colors pulse-gold"
          >
            <Calendar className="h-5 w-5" />
            {t(hero.cta.book)}
          </Link>
          <a
            href="tel:+17738183020"
            className="inline-flex items-center justify-center gap-2.5 border border-primary/60 text-primary px-12 py-5 rounded-md text-lg font-semibold hover:bg-primary/10 transition-colors"
          >
            <Phone className="h-5 w-5" />
            {t(hero.cta.call)}
          </a>
          <a
            href="https://wa.me/17738183020"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2.5 border border-border/50 text-foreground/80 px-12 py-5 rounded-md text-lg font-semibold hover:border-primary/40 hover:text-primary transition-colors"
          >
            <MessageCircle className="h-5 w-5" />
            {t(hero.cta.whatsapp)}
          </a>
        </div>

        {/* Trust Markers */}
        <div className="flex flex-wrap gap-10 mb-16 opacity-0 animate-fade-in" style={{ animationDelay: "0.8s", animationFillMode: "forwards" }}>
          {hero.trustMarkers.map((marker, i) => (
            <div key={i} className="flex items-center gap-2.5 text-base text-muted-foreground">
              <div className="h-px w-6 bg-primary/50" />
              <span>{t(marker)}</span>
            </div>
          ))}
        </div>

        {/* Proof Bar */}
        <div className="opacity-0 animate-fade-in max-w-4xl" style={{ animationDelay: "1s", animationFillMode: "forwards" }}>
          <ProofBar compact />
        </div>
      </div>
    </section>
  );
}
